import {
  Action,
  RewardsScreen,
  UserJourney,
  useRewardsAnalytics,
} from "@/context/AnalyticsProvider";
import type { Rarity } from "@/domain/gems-game/domain/lib/Earning";
import { RARITY_LEVELS } from "@/utils/rewards";
import { onDarkBase } from "@biom3/design-tokens";
import { Body } from "@biom3/react";
import { Box } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ChevronDown, CrownIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { SEGMENT_LENGTHS } from "./KeyProgressBar";
import { KeyProgressBar } from "./KeyProgressBar";
import { RarityKeyIcon } from "./RarityKeyIcon";
import { RarityText } from "./RarityText";
import { WeeklyRewardsInfoDropdown } from "./WeeklyRewardsInfoDropdown";
import { useWeeklyRewards } from "./WeeklyRewardsProvider";

export const HeaderProgressBar = () => {
  const { track } = useRewardsAnalytics();
  const { legendaryProgressBar } = useFlags();
  const { predictedStats } = useWeeklyRewards();

  const tierIndex = useMemo(() => {
    const idx = RARITY_LEVELS.indexOf(
      (predictedStats?.predictedRarity || "Common") as Rarity,
    );
    return idx === -1 ? 0 : idx;
  }, [predictedStats]);

  const progressPercentage = useMemo(() => {
    if (typeof predictedStats?.percentageToNextTier === "number") {
      return Math.max(0, Math.min(100, predictedStats.percentageToNextTier));
    }
    return 0;
  }, [predictedStats]);

  const currentRarity: Rarity = RARITY_LEVELS[tierIndex];

  const cumulativeBefore = RARITY_LEVELS.slice(0, tierIndex).reduce(
    (sum, r) => sum + SEGMENT_LENGTHS[r],
    0,
  );

  const absoluteProgress = Math.max(
    1,
    Math.min(
      99,
      currentRarity === "Legendary" && legendaryProgressBar
        ? progressPercentage
        : cumulativeBefore +
            (progressPercentage / 100) * SEGMENT_LENGTHS[currentRarity],
    ),
  );

  const [anchorPosition, setAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const weeklyPoints = predictedStats?.weeklyPoints;

  const totalGamesPlayed = useMemo(() => {
    const games = new Set<string>();
    // Filter games that have at least one quest where canStillCompleteThisPeriod is false
    for (const game of predictedStats?.games?.filter((game) =>
      game.quests.some((quest) => !quest.canStillCompleteThisPeriod),
    ) || []) {
      games.add(game.id);
    }
    return games.size;
  }, [predictedStats]);

  const handleBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (anchorPosition) {
      setAnchorPosition(null);
    } else {
      // Track the progress bar click and dropdown opening
      track({
        action: Action.OPENED,
        extras: {
          section: "ProgressBar",
          currentRarity: currentRarity as string,
          totalGamesPlayed: totalGamesPlayed ?? 0,
          weeklyPoints,
          progressPercentage,
          screen: RewardsScreen.PROGRESS_BAR,
          userJourney: UserJourney.CLAIM,
        },
      });

      // Calculate position relative to progress bar element
      if (progressBarRef.current) {
        const rect = progressBarRef.current.getBoundingClientRect();
        setAnchorPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + rect.width / 2 + window.scrollX,
        });
      }
    }
  };

  const handleClose = () => setAnchorPosition(null);

  return (
    <Box
      width="100%"
      maxWidth="1200px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={onDarkBase.spacing.x1}
      height={{ xs: 60, md: 78 }}
    >
      <WeeklyRewardsInfoDropdown
        userStats={predictedStats}
        currentRarity={currentRarity}
        anchorPosition={anchorPosition}
        open={Boolean(anchorPosition)}
        onCloseExtern={handleClose}
      />

      <Box
        ref={progressBarRef}
        position="relative"
        width="100%"
        onClick={handleBarClick}
        sx={{
          cursor: "pointer",
          borderRadius: "18px",
          border: {
            xs: `8px solid ${onDarkBase.color.neutral[500]}`,
            md: `14px solid ${onDarkBase.color.neutral[500]}`,
          },
          zIndex: 1,
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <KeyProgressBar
            shouldShowLegendaryOnly={
              currentRarity === "Legendary" && legendaryProgressBar
            }
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: {
              xs: "translate(-50%, -60%)",
              md: "translate(-50%, -90%)",
            },
            px: onDarkBase.spacing.x4,
            py: onDarkBase.spacing.x2,
            backgroundColor: onDarkBase.color.neutral[500],
            clipPath:
              "polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%)",
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              position: "relative",
              top: { xs: "-13px", md: "-6px" },
            }}
          >
            <Body size="xxSmall" sx={{ fontWeight: "bold" }}>
              {predictedStats?.hasKeyForSweepstake
                ? "YOUR KEY"
                : "YOUR NEXT KEY"}{" "}
              /{" "}
              <RarityText
                rarity={currentRarity}
                sx={{ textTransform: "uppercase" }}
              />
            </Body>
          </Box>
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: {
              xs: "translate(-50%, 60%)",
              md: "translate(-50%, 85%)",
            },
            px: onDarkBase.spacing.x6,
            py: onDarkBase.spacing.x2,
            backgroundColor: onDarkBase.color.neutral[500],
            clipPath:
              "polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%)",
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              position: "relative",
              top: { xs: "10px", md: "6px" },
            }}
          >
            <Body size="xxSmall" sx={{ fontWeight: "bold" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: "4px", md: "8px" },
                }}
              >
                {weeklyPoints ?? 0}{" "}
                <CrownIcon className="w-3 h-3 text-yellow-400" />
                <ChevronDown
                  className="h-6 w-6"
                  style={{
                    fontWeight: "bold",
                    color: "#ffffff",
                  }}
                />
              </Box>
            </Body>
          </Box>
        </Box>

        {predictedStats && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: `${absoluteProgress}%`,
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              zIndex: 4,
            }}
          >
            <Box
              sx={{
                width: { xs: 18, md: 32 },
                height: { xs: 18, md: 32 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RarityKeyIcon rarity={currentRarity} size={32} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
